import {
  Box,
  Typography,
  Divider,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField as MuiTextField,
} from '@material-ui/core';
import { FormStore } from '@roc/feature-app-core';
import {
  citizenshipStatusOptions,
  DateFormat,
  isNotBlank,
} from '@roc/feature-utils';
import {
  AddressDetailsField,
  createCurrencyField,
  createNumberFormatField,
  createNumberFormatFieldNoDecimalsAndWithLimit,
  createRadioField,
  createSelectField,
  createSliderField,
  createTextField,
  DateField,
  FieldLabel,
  NumberFormat,
  RadioField,
  SelectField,
  Slider,
  TextField,
  trueFalseOptions,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { LoanSubType } from '../../utils/constants';
import { formatCurrency } from '@roc/ui/utils';
import { BorrowerFormStore } from '../../stores/common/borrowerFormStore';
import { ItemDisplay } from './itemDisplay';
import { getExperienceOptions } from '../../utils/utils';

export const BorrowerEmail = observer(
  ({
    store,
    disabled,
    required,
  }: {
    store: BorrowerFormStore;
    disabled?: boolean;
    required?: boolean;
  }) => {
    const fieldName = 'emailAddress';
    const field = store.form.fields[fieldName];

    return (
      <TextField
        label="Email"
        name={fieldName}
        testId={fieldName}
        value={field.value}
        onChange={e => store.onFieldChange(fieldName, e.target.value)}
        variant="outlined"
        fullWidth
        error={!store.form.meta.isValid && isNotBlank(field.error)}
        helperText={!store.form.meta.isValid && field.error}
        disabled={disabled}
        required={required}
      />
    );
  }
);

export const PhoneNumber = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const fieldName = 'phoneNumber';
    const field = store.form.fields[fieldName];
    return (
      <NumberFormat
        testId={fieldName}
        variant="outlined"
        customInput={TextField}
        format={'###-###-####'}
        mask="_"
        label={'Phone Number'}
        value={field.value}
        onChange={e => store.onFieldChange(fieldName, e.target.value)}
        error={!store.form.meta.isValid && isNotBlank(field.error)}
        helperText={!store.form.meta.isValid && field.error}
        fullWidth
      />
    );
  }
);

export const DateOfBirth = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const fieldName = 'dateOfBirth';
    const field = store.form.fields[fieldName];
    const { isNewBorrower } = store;
    return (
      <DateField
        testId={fieldName}
        inputVariant="outlined"
        format={DateFormat.MMDDYYYY}
        label={'Date of Birth'}
        value={field.value}
        onChange={date => store.onFieldChange(fieldName, date)}
        disableFuture={true}
        fullWidth
        error={!store.form.meta.isValid && isNotBlank(field.error)}
        helperText={!store.form.meta.isValid && field.error}
        standaloneLabel={false}
        disabled={isNewBorrower}
      />
    );
  }
);

export const SocialSecurityNumber = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const fieldName = 'ssn';
    const field = store.form.fields[fieldName];
    const { isNewBorrower } = store;
    return (
      <TextField
        label="Social Security Number"
        name={fieldName}
        testId={fieldName}
        value={field.value}
        onChange={e => store.onFieldChange(fieldName, e.target.value)}
        variant="outlined"
        fullWidth
        error={!store.form.meta.isValid && isNotBlank(field.error)}
        helperText={!store.form.meta.isValid && field.error}
        disabled={isNewBorrower}
      />
    );
  }
);

export const CitizenshipStatus = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const fieldName = 'citizenshipStatus';
    const field = store.form.fields[fieldName];
    const { isNewBorrower } = store;
    return (
      <SelectField
        label="Citizenship Status"
        name={fieldName}
        testId={fieldName}
        value={field.value}
        onChange={value => store.onFieldChange(fieldName, value)}
        options={citizenshipStatusOptions}
        disabled={isNewBorrower}
        variant="outlined"
        fullWidth
        error={!store.form.meta.isValid && isNotBlank(field.error)}
        helperText={!store.form.meta.isValid && field.error}
      />
    );
  }
);

export const BorrowerAddress = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const fieldName = 'address';
    const field = store.form.fields[fieldName];
    const { isNewBorrower } = store;
    return (
      <AddressDetailsField
        name={fieldName}
        value={field.value}
        onChange={value => store.onFieldChange(fieldName, value)}
        error={!store.form.meta.isValid && isNotBlank(field.error)}
        helperText={!store.form.meta.isValid && field.error}
        disabled={isNewBorrower}
      />
    );
  }
);

const usePersonalGuarantorStyles = makeStyles(theme => ({
  infoMessage: {
    fontStyle: 'italic',
    marginTop: '0rem',
    marginLeft: '1rem',
    color: 'gray',
    fontSize: '12px',
  },
}));

export const PersonalGuarantor = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const classes = usePersonalGuarantorStyles();
    const fieldName = 'personalGuarantor';
    const field = store.form.fields[fieldName];
    const { isNewBorrower } = store;
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value}
                onChange={(e, checked) =>
                  store.onFieldChange(fieldName, checked)
                }
                color="primary"
              />
            }
            label={'This borrower is a personal guarantor for the loan.'}
            disabled={isNewBorrower}
          />
        </Grid>
        <Grid item>
          <Typography variant="body2" className={classes.infoMessage}>
            If yes, the borrower's credit and background reports are needed. if
            no, only a background report is needed.
          </Typography>
        </Grid>
      </Grid>
    );
  }
);

const useFirstNameAutocompleteFieldStyles = makeStyles(theme => ({
  autocomplete: {
    '& .MuiAutocomplete-endAdornment': {
      display: 'none',
    },
  },
}));

export const FirstNameAutocompleteField = observer(
  ({
    store,
    optional,
    disabled,
    required,
    loanSubtype,
  }: {
    store: BorrowerFormStore;
    optional?: boolean;
    disabled?: boolean;
    required?: boolean;
    loanSubtype?: string;
  }) => {
    const inputFieldName = 'firstName';
    const formFields = store.form.fields;
    const inputField = formFields[inputFieldName];
    const options = store.borrowerOptions;
    const classes = useFirstNameAutocompleteFieldStyles();

    const onChange = firstName => {
      store.onFieldChange(inputFieldName, firstName);
      if (firstName) {
        store.findBorrowersByNameDebounced(firstName);
      }
    };

    return (
      <Box className={classes.autocomplete}>
        <Autocomplete
          id={`autocomplete-${store.key}`}
          inputValue={inputField?.value}
          onInputChange={(e, value) => {
            // Ignore clear
            if (e?.nativeEvent?.type === 'input') {
              onChange(value);
            }
          }}
          disabled={disabled}
          options={options}
          renderInput={params => (
            <MuiTextField
              variant={'outlined'}
              label={'First Name'}
              required={required}
              error={isNotBlank(inputField?.error)}
              helperText={inputField?.error}
              {...params}
            />
          )}
          getOptionLabel={(option: any) => option?.label ?? ''}
          getOptionSelected={(option: any, value) =>
            option.label === value.label
          }
          onChange={(event, opt) => store.fetchBorrower(opt.value)}
          fullWidth
          PopperComponent={options.length ? undefined : () => null}
        />
      </Box>
    );
  }
);

export const LastName = observer(
  ({
    store,
    optional,
    disabled,
  }: {
    store: BorrowerFormStore;
    optional?: boolean;
    disabled?: boolean;
  }) => {
    const { isNewBorrower } = store;

    return (
      <>
        {createTextField({
          label: 'Last Name',
          fieldName: 'lastName',
          testId: 'lastName',
          store,
          standaloneLabel: false,
          disabled: disabled || isNewBorrower,
        })}
      </>
    );
  }
);

export const IsRealEstateBrokerAgent = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const { isNewBorrower } = store;
    return (
      <>
        {createRadioField({
          label: 'Is the Borrower a Real Estate Broker/Agent?',
          fieldName: 'isRealEstateBrokerAgent',
          testId: 'isRealEstateBrokerAgent',
          store,
          options: trueFalseOptions,
          row: true,
          standaloneLabel: false,
          disabled: isNewBorrower,
        })}
      </>
    );
  }
);

export const IsTheBorrowerAGeneralContractor = observer(
  ({ store }: { store: BorrowerFormStore }) => {
    const { isNewBorrower } = store;
    return (
      <>
        {createRadioField({
          label: 'Is the Borrower a General Contractor?',
          fieldName: 'isGeneralContractor',
          testId: 'isGeneralContractor',
          store,
          options: trueFalseOptions,
          row: true,
          standaloneLabel: false,
          disabled: isNewBorrower,
        })}
      </>
    );
  }
);

export const FicoScore = observer(
  ({ store, disabled }: { store: BorrowerFormStore; disabled?: boolean }) => {
    const { isNewBorrower } = store;

    return (
      <>
        {createNumberFormatFieldNoDecimalsAndWithLimit({
          label: 'Fico Score',
          fieldName: 'ficoScore',
          testId: 'ficoScore',
          store,
          standaloneLabel: false,
          showErrors: store.form.meta.showErrors,
          disabled: disabled || isNewBorrower,
        })}
      </>
    );
  }
);

export const Experience = observer(
  ({
    store,
    disabled,
    loanSubtype,
  }: {
    store: BorrowerFormStore;
    disabled?: boolean;
    loanSubtype?: LoanSubType;
  }) => {
    const { isNewBorrower } = store;

    return (
      <>
        {createSelectField({
          store,
          testId: 'experience',
          fieldName: 'experience',
          label: 'Number of Verified Investment Properties',
          options: getExperienceOptions(loanSubtype),
          standaloneLabel: false,
          disabled: disabled || isNewBorrower,
          isRequired: true,
        })}
      </>
    );
  }
);

export const Assets = observer(
  ({ store, optional }: { store: BorrowerFormStore; optional?: boolean }) => {
    const { isNewBorrower } = store;
    return (
      <>
        {createCurrencyField({
          label: 'Assets',
          fieldName: 'assets',
          testId: 'assets',
          store,
          standaloneLabel: false,
        })}
      </>
    );
  }
);

export const Liabilities = observer(
  ({ store, optional }: { store: BorrowerFormStore; optional?: boolean }) => {
    const { isNewBorrower } = store;
    return (
      <>
        {createCurrencyField({
          label: 'Liabilities',
          fieldName: 'liabilities',
          testId: 'liabilities',
          store,
          standaloneLabel: false,
          disabled: isNewBorrower,
        })}
      </>
    );
  }
);

export const NetWorth = observer(
  ({ store, optional }: { store: BorrowerFormStore; optional?: boolean }) => {
    const assets = store.form.fields.assets.value
      ? store.form.fields.assets.value
      : 0;
    const liabilities = store.form.fields.liabilities.value
      ? store.form.fields.liabilities.value
      : 0;
    const netWorth = formatCurrency(assets - liabilities);

    return <ItemDisplay label="Net Worth" value={netWorth} showBackground />;
  }
);

const useSliderStyles = makeStyles(theme => ({
  slider: {
    marginTop: '2.5em',
    marginLeft: '2em',
    width: '85%',
  },
  helperText: {
    marginTop: theme.spacing(2),
  },
}));

export const OwnershipPercentage = observer(({ store }) => {
  const formFields = store.form.fields;
  const classes = useSliderStyles();
  const fieldName = 'percentageOfOwnership';
  const { isNewBorrower } = store;

  return (
    <>
      <FieldLabel>
        {'What percentage of the borrowing entity does this borrower own?*'}
      </FieldLabel>
      <Slider
        testId={fieldName}
        key={formFields.borrowerId.value}
        onChangeCommitted={(event, value) =>
          store.onFieldChange(fieldName, value)
        }
        className={classes.slider}
        value={formFields[fieldName].value}
        valueLabelDisplay="on"
        valueLabelFormat={value => <div>{formFields[fieldName].value}%</div>}
        disabled={isNewBorrower}
        marks={[
          {
            value: 0,
            label: '0%',
          },
          {
            value: 100,
            label: '100%',
          },
        ]}
        step={1}
        min={0}
        max={100}
      />
    </>
  );
});
